import type { ImageProps as NextImageProps } from 'next/image';
import type { LinkProps } from 'next/link';
import type { FC, ReactNode } from 'react';
import { type PropsWithChildren, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Navigation, Pagination } from 'swiper';
import { SwiperSlide } from 'swiper/react';
import type { Maybe } from 'yup';

import { getLinkAriaLabel } from '@/utils/urlUtils';

import { Box, type ResponsiveValue } from '@/components/Box';
import type { ButtonProps } from '@/components/LegacyButton';
import { LegacyButton } from '@/components/LegacyButton';
import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import { ProductLabel } from '@/components/ProductLabel';
import type { TypographyScales } from '@/components/Typography';
import { Typography } from '@/components/Typography';
import type { ProductMediaFragment } from '@/graphql/saleor/api';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import { useWishList } from '@/hooks/useWishlist';
import { EVENT_NAME } from '@/lib/gtm/const';
import type { ProductTypeOptions } from '@/lib/product';
import { getVariantCTA } from '@/lib/product';
import { generateDataTestId } from '@/lib/tests';
import type { CtaType, WithChildren, WithHTMLProp } from '@/lib/types';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { FontWeights } from '@/theme/types';
import type { WishlistItem } from '@/views/WishListView/types';

import { Loader } from '../Loader';
import { Visibility } from '../Visibility';
import { Name, Price, Share, Sku } from './components';

import { productCardMessages } from './messages';
import * as S from './ProductCard.styles';

export interface ProductCardProps extends WithChildren {
  backgroundColor?: string;
  cardClickHandler?: () => void;
  className?: string;
  cta?: ReactNode;
  ctaType?: 'hovered' | 'bottom';
  href?: LinkProps['href'];
  sku?: string;
  style?: React.CSSProperties;
  topSlot?: ReactNode;
  type?: string;
}
export interface ProductFavoriteProps {
  isFavIconWhite?: boolean;
  isHovered?: boolean;
  name?: string | null;
  price: number | undefined;
  sku: string;
  theCurrency: string | undefined;
  type: string;
}
export interface ExpanderProps {
  onClick?: () => void;
}
export interface StickyIconProps extends Pick<ButtonProps, 'onClick'> {
  index?: number;
  isStickyType?: boolean;
}

export const ProductCard = ({
  backgroundColor,
  cardClickHandler,
  children,
  className,
  cta,
  ctaType = 'hovered',
  href,
  sku,
  style,
  topSlot,
}: ProductCardProps) => {
  const { sendEcommerceEventBySkus } = useGoogleAnalytics();
  const { pathname } = useRouter();
  const isWishListPage = pathname?.includes('wishlist');

  const handleCardClick = () => {
    if (sku) {
      sendEcommerceEventBySkus({
        event: EVENT_NAME.SELECT_ITEM,
        skus: [sku as string],
      });

      if (cardClickHandler) {
        cardClickHandler();
      }
    }
  };

  return (
    <S.Wrapper
      $backgroundColor={backgroundColor}
      $ctaType={ctaType}
      className={className}
      data-test-id='ProductCardWrapper'
      style={style}
      {...generateDataTestId('productCard')}
    >
      {topSlot}
      <S.ProductLinkArea
        as={href ? LinkWithLocale : 'div'}
        {...(href && { href })}
        aria-label={getLinkAriaLabel(href ?? '')}
        data-test-id='ProductCardProductLinkArea'
        data-testid='ProductCard'
        onClick={handleCardClick}
      >
        {children}
      </S.ProductLinkArea>
      {cta ??
        (isWishListPage ? (
          <S.CtaPlaceholder
            data-testid='ctaPlaceHolder'
            size='s'
            type='button'
            variant='link'
          />
        ) : null)}
    </S.Wrapper>
  );
};

export interface ImageProps
  extends Pick<NextImageProps, 'src' | 'alt' | 'onMouseOver' | 'onMouseOut'> {
  $noPadding?: boolean;
  className?: string;
  disableLoader?: boolean;
  height?: number;
  loading?: 'eager' | 'lazy';
  priority?: boolean;
  productType?: ProductTypeOptions;
  scale?: number;
  scaleRatio?: number;
  sizes?: string;
  width?: number;
  withBackground?: boolean;
  withBackgroundPlaceholderBackground?: boolean;
  zoom?: number;
}

const Image = ({
  $noPadding,
  alt,
  className,
  height = 350,
  loading,
  priority,
  productType,
  scale,
  scaleRatio,
  sizes = '50vw',
  src,
  width = 260,
  withBackground,
  withBackgroundPlaceholderBackground,
  zoom,
  ...props
}: ImageProps) => {
  return (
    <S.Img
      $noPadding={$noPadding}
      $scale={scale}
      $scaleRatio={scaleRatio}
      $withBackground={!!withBackground}
      $zoom={zoom}
      alt={alt}
      aria-hidden
      className={className}
      height={height}
      loaderHeight={330}
      loading={loading ?? 'eager'}
      priority={priority}
      productType={productType}
      sizes={sizes}
      src={src ?? ''}
      width={width}
      {...props}
      data-test-id='ProductCardImage'
      {...generateDataTestId('productCard', 'image')}
      showFallbackPlaceholder
      showPlaceholderBackground={withBackgroundPlaceholderBackground}
    />
  );
};

export interface CTAProps
  extends Pick<
      ButtonProps,
      'isProcessing' | 'disabled' | 'onClick' | 'isFluid' | 'variant' | 'size'
    >,
    WithHTMLProp<'className'> {
  cta: CtaType | undefined;
}

export const CTA = ({ cta, size = 's', ...props }: CTAProps) => {
  const { formatMessage } = useIntl();
  if (!cta)
    return (
      <Loader
        className={props.className}
        height={40}
        noMargin={true}
        size='sm'
        style={{ marginTop: 16, minHeight: 40 }}
      />
    );

  return (
    <Visibility handleDisplay={false}>
      <LegacyButton
        beforeContent
        data-text={getVariantCTA(formatMessage, cta)}
        label={getVariantCTA(formatMessage, cta)}
        size={size}
        type='button'
        variant={cta === 'NO_CTA' ? 'link' : 'outlined'}
        {...props}
        data-test-id='ProductCardCTA'
        width='100%'
        {...generateDataTestId('productCard', 'CTA')}
      />
    </Visibility>
  );
};

const AdditionalInfo = ({ children }: PropsWithChildren) => {
  return (
    <Typography
      color='secondaryGrey'
      data-test-id='ProductCardAdditionalInfo'
      scale='p3'
      {...generateDataTestId('productCard', 'additionalInfo')}
    >
      {children}
    </Typography>
  );
};
const CloseIcon = ({ onClick }: Pick<ButtonProps, 'onClick'>) => {
  return (
    <Box
      data-test-id='ProductCardCloseIcon'
      data-testid='ProductCardCloseIcon'
      onClickCapture={onClick}
    >
      <S.CloseIcon {...generateDataTestId('productCard', 'CloseIcon')} />
    </Box>
  );
};

export interface ShareIconProps {
  onClick?: () => void;
  positionRight?: boolean;
}

const ShareIcon = ({ onClick, positionRight }: ShareIconProps) => {
  return (
    <S.ShareIcon
      data-testid='shareIcon'
      onClick={onClick}
      positionRight={positionRight}
    />
  );
};

const Sticky = ({ index, isStickyType, onClick }: StickyIconProps) => {
  return isStickyType ? (
    <Box data-test-id={`ProductCardPinedIcon-${index}`} onClick={onClick}>
      <S.PinedIcon
        {...generateDataTestId('productCard', `PinedIcon${index}`)}
      />
    </Box>
  ) : (
    <Box data-test-id={`ProductCardPinIcon-${index}`} onClick={onClick}>
      <S.PinIcon {...generateDataTestId('productCard', `PinIcon${index}`)} />
    </Box>
  );
};

const Favorite: FC<ProductFavoriteProps> = ({
  isFavIconWhite,
  isHovered,
  name,
  price,
  sku,
  theCurrency,
  type,
}) => {
  const { formatMessage } = useIntl();
  const [loading, setLoading] = useState(false);
  const { country } = useRegionProvider();
  const formatData: WishlistItem = {
    price: price ?? 0,
    sku,
    theCurrency,
    type: type?.slice(0, 1).toUpperCase() + type?.slice(1).toLowerCase(),
  };
  const {
    addItemToLocalWishlist,
    addItemToServerWishlist,
    isItemInLocalWishlist,
    removeItemFromLocalWishlist,
    removeItemFromServerWishlist,
  } = useWishList({ countryCode: country.code });
  const favoriteToLocal = isItemInLocalWishlist(formatData.sku);
  const {
    snapshot: { isAuthenticated },
  } = useUser();
  const [isLineFavicon, setIsLineFavicon] = useState(false);
  const FaviconClick = async (e: { preventDefault: () => void }) => {
    setLoading(true);
    e.preventDefault();
    if (favoriteToLocal) {
      if (isAuthenticated) {
        removeItemFromServerWishlist(formatData.sku as string);
      }
      removeItemFromLocalWishlist(formatData.sku);
      setIsLineFavicon(false);
    } else {
      if (isAuthenticated) {
        addItemToServerWishlist(formatData);
      }
      addItemToLocalWishlist(formatData);
      setIsLineFavicon(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (favoriteToLocal) {
      setIsLineFavicon(favoriteToLocal);
    }
  }, [favoriteToLocal]);
  if (loading) return <></>;
  return isLineFavicon ? (
    <S.FavIconWrapper
      aria-label={formatMessage(productCardMessages.removeFromWishList, {
        name: name ?? sku,
      })}
      onClick={(event) => {
        event.stopPropagation();
        FaviconClick(event);
      }}
    >
      <S.FavIconed
        data-test-id='ProductCardFavIconed'
        name='heart'
        size='l'
        variant='regular'
        {...generateDataTestId('productCard', 'FavIconed')}
      />
    </S.FavIconWrapper>
  ) : (
    <S.FavIconWrapper
      aria-label={formatMessage(productCardMessages.addToWishList, {
        name: name ?? sku,
      })}
      onClick={(event) => {
        event.stopPropagation();
        FaviconClick(event);
      }}
    >
      <S.FavIcon
        $isFavIconWhite={isFavIconWhite}
        $isHovered={isHovered}
        data-test-id='ProductCardFavIcon'
        {...generateDataTestId('productCard', 'FavIcon')}
      />
    </S.FavIconWrapper>
  );
};

const LugSize = ({ children }: PropsWithChildren) => {
  return (
    <S.LugSize
      {...generateDataTestId('productCard', 'lugSize')}
      data-test-id='ProductCardLugSize'
    >
      {children}
    </S.LugSize>
  );
};

export interface LabelProps {
  text: Maybe<React.ReactNode>;
}

const Label = ({ text }: LabelProps) => {
  return (
    <S.LabelWrapper data-test-id='ProductCardLabel'>
      <ProductLabel
        text={text}
        {...generateDataTestId('productCard', 'label')}
      />
    </S.LabelWrapper>
  );
};

export interface ProductTypographyProps {
  scale?: TypographyScales;
  text: string;
  weight?: ResponsiveValue<FontWeights>;
}

const ProductTypography = ({
  scale = 'h5',
  text,
  weight = 'regular',
}: ProductTypographyProps) => {
  return (
    <Typography fontWeight={weight} scale={scale}>
      {text}
    </Typography>
  );
};
const StrapNotCompare = ({ text }: ProductTypographyProps) => {
  return (
    <S.StrapNotCompare data-test-id='ProductCardStrapNotCompare'>
      {' '}
      {text}
    </S.StrapNotCompare>
  );
};
export interface ProductSliderProps {
  alt?: string;
  className?: string;
  media: Array<ProductMediaFragment>;
  scaleRatio?: number;
  sizes?: string;
}
const Slider = ({
  alt,
  className,
  media,
  scaleRatio = 1,
  sizes,
}: ProductSliderProps) => {
  return (
    <S.StyledSwiper
      className={className}
      loop={true}
      modules={[Navigation, Pagination]}
      navigation
      pagination
    >
      {media.map((item, index) => {
        return (
          <SwiperSlide key={item.url}>
            <S.Img
              $scaleRatio={scaleRatio}
              $withBackground={false}
              alt={alt ?? item.alt}
              aria-hidden
              data-test-id={`ProductCardImage-${index}`}
              height={0}
              loading='eager'
              sizes={sizes ?? '50vw'}
              src={item.url ?? ''}
              width={0}
              {...generateDataTestId('productCard', 'image')}
            />
          </SwiperSlide>
        );
      })}
    </S.StyledSwiper>
  );
};

export interface ProductAlternateProps {
  alt?: string;
  className?: string;
  isHovered?: boolean;
  loading?: 'eager' | 'lazy';
  media?: ProductMediaFragment;
  mediaTwo?: ProductMediaFragment;
  priority?: boolean;
  productType?: string;
  scaleRatio?: number;
  sizes?: string;
  withBackground?: boolean;
  withBackgroundPlaceholderBackground?: boolean;
}

const AlternateImg = ({
  alt,
  isHovered,
  loading = 'eager',
  media,
  mediaTwo,
  scaleRatio = 1,
  sizes,
  withBackground = false,
  ...props
}: ProductAlternateProps) => {
  const renderImage = (src?: string, alt?: string, isVisible?: boolean) =>
    isVisible && (
      <S.Img
        $scaleRatio={scaleRatio}
        $withBackground={withBackground}
        alt={alt}
        aria-hidden
        height={350}
        loaderHeight={330}
        loading={loading}
        sizes={sizes ?? '50vw'}
        src={src ?? ''}
        width={260}
        {...props}
        data-test-id='ProductCardImage'
        {...generateDataTestId('productCard', 'image')}
        showFallbackPlaceholder
        showPlaceholderBackground
      />
    );
  return (
    <>
      {renderImage(media?.url, media?.alt || alt, !isHovered)}
      {renderImage(mediaTwo?.url, mediaTwo?.alt || alt, isHovered)}
    </>
  );
};

const Zone: FC<PropsWithChildren> = ({ children }) => {
  return <S.CardZone>{children}</S.CardZone>;
};

const CardZoneLabelPriceRefGroup: FC<PropsWithChildren> = ({ children }) => {
  return (
    <S.CardZoneSkuLabelPriceGroup>{children}</S.CardZoneSkuLabelPriceGroup>
  );
};

ProductCard.Image = Image;
ProductCard.Details = S.Details;
ProductCard.ShareIcon = ShareIcon;
ProductCard.Sticky = Sticky;
ProductCard.Sku = Sku;
ProductCard.Name = Name;
ProductCard.Price = Price;
ProductCard.CTA = CTA;
ProductCard.AdditionalInfo = AdditionalInfo;
ProductCard.Favorite = Favorite;
ProductCard.CloseIcon = CloseIcon;
ProductCard.Label = Label;
ProductCard.LugSize = LugSize;
ProductCard.Typography = ProductTypography;
ProductCard.StrapNotCompare = StrapNotCompare;
ProductCard.Slider = Slider;
ProductCard.AlternateImg = AlternateImg;
ProductCard.Share = Share;
ProductCard.Zone = Zone;
ProductCard.CardZoneLabelPriceRefGroup = CardZoneLabelPriceRefGroup;

ProductCard.displayName = 'ProductCard';
